/*----------------------------------
- HOME: DEMO BROWSE PANEL
----------------------------------*/

/**
 * The first tab of the demo window, "Find a name" (`.bw`): Browse's layout without its rail, drawn around the real
 * public table. The left column holds the prompt field, Deep Search with the catalog's size and the filter groups; the
 * bar holds the search tab, the table's controls and "Radar on this search". The table itself is `HomeDomainsPreview`:
 * the ten rows of the bare `/domains` search through the listing's own block (ADR-0028).
 *
 * @docs docs/features/website-landing-experience
 * @adr  ADR-0028
 * @adr  ADR-0110
 * @rule Every control of the frame opens sign-up with the whole search (the preview's See all handoff, its catalogued
 *       `website_landing_see_all` id): the frame is a demonstration and never filters, sorts or pages the rows.
 * @rule The mock's pager is not drawn (Home's preview has no pagination, ADR-0028). Its stats strip is drawn with the
 *       four figures Product serves for the search, never figures computed from the ten rows (Gaetan's default 6 of
 *       2026-11-03, `HomeBrowseStrip`).
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/


import { Button } from '@uniquedomains/components/kit/button/Button';
import { IconButton } from '@uniquedomains/components/kit/icon-button/IconButton';
import { Switch } from '@uniquedomains/components/kit/switch/Switch';

import Icon from '@/client/components/ui/icon/Icon';
import { HOME_BROWSE_COPY } from '@/client/catalogs/landing/home/homeCopy';
import HomeDomainsPreview from '@/client/pages/landing/components/sections/product/HomeDomainsPreview';
import { formatCompactStat } from '@/client/pages/landing/lib/formatCompactStat';
import useHomeDomainsPreviewHandoff from '@/client/pages/landing/lib/useHomeDomainsPreviewHandoff';
import type { HomeDomainsPreviewData } from '@/client/pages/landing/lib/homeDomainsPreview';
import { readHomeDemoStripFigures } from '@/client/pages/landing/lib/homeDemoStrip';
import HomeBrowseStrip from './HomeBrowseStrip';
import HomeSearchField from './HomeSearchField';

/*----------------------------------
- TYPES
----------------------------------*/

type HomeBrowsePanelProps = {
    preview: HomeDomainsPreviewData;
    /** The catalog's size (`stats.totalDomains`); 0 drops the count beside Deep Search. */
    totalDomains: number;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

/**
 * The rows scroll inside the window: the window keeps the mock's fixed height (`--ud-size-window`) and `.bw-tbl` clips.
 * The block's foot (the legend and See all) is pinned at the bottom of the scroll area (home.css, `.bw-tbl .wp-tfoot`
 * sticky; web-frame-12), so only the rows move, with the strip under it. Inline because it places the block in this
 * frame only.
 */
const TABLE_SCROLLER_STYLE = 'position:absolute;inset:0;overflow:auto';

/*----------------------------------
- COMPONENT
----------------------------------*/

const HomeBrowsePanel = ({ preview, totalDomains }: HomeBrowsePanelProps) => {
    const { handleSeeAll } = useHomeDomainsPreviewHandoff({ search: preview.search });
    const copy = HOME_BROWSE_COPY;
    // A filter group is a row of Browse's rail; here it is a control that opens sign-up, so it answers the keyboard too.
    return (
        <div class="bw">
            {/* A named region, not an `aside`: inside the page's `main` a complementary landmark is nested (axe
                `landmark-complementary-is-top-level`, batch 7 review's leftover, batch 6 S2b). */}
            <section class="bw-f" aria-label={copy.filters}>
                <div class="bw-mode">
                    <div class="ud-seg" aria-hidden="true">
                        <span class="ud-seg__item is-on">{copy.modes[0]}</span>
                        <span class="ud-seg__item">{copy.modes[1]}</span>
                    </div>
                    <IconButton label={copy.reset} icon={<Icon name="refresh" size={15} />} onClick={handleSeeAll} />
                </div>
                <div class="bw-prompt">
                    <HomeSearchField />
                    <div class="bw-deep">
                        <Switch size="sm" on={false} label={copy.deepSearch} onChange={handleSeeAll} />
                        {copy.deepSearch}
                        {totalDomains > 0 ? (
                            <em>
                                <b>{formatCompactStat(totalDomains)}</b>
                                {copy.namesUnit}
                            </em>
                        ) : null}
                    </div>
                </div>
                <ul class="bw-groups">
                    {copy.groups.map((group) => (
                        <li key={group.label}>
                            <button type="button" onClick={handleSeeAll}>
                                <Icon name={group.icon} size={15} class="i" />
                                {group.label}
                                <Icon name="chevron-down" size={14} class="i chev" />
                            </button>
                        </li>
                    ))}
                </ul>
            </section>
            <section class="bw-r" aria-label={copy.region}>
                <div class="bw-bar">
                    <span class="bw-tab">
                        <Icon name="search" size={14} />
                        {copy.tab}
                    </span>
                    <IconButton variant="plain" label={copy.newTab} icon={<Icon name="plus" size={15} />} onClick={handleSeeAll} />
                    <span class="bw-sp" />
                    <IconButton label={copy.filters} icon={<Icon name="sliders" size={15} />} onClick={handleSeeAll} />
                    <IconButton label={copy.export} icon={<Icon name="download" size={15} />} onClick={handleSeeAll} />
                    <IconButton label={copy.share} icon={<Icon name="share" size={15} />} onClick={handleSeeAll} />
                    <Button variant="ink" icon={<i class="bd" aria-hidden="true" />} onClick={handleSeeAll}>
                        {copy.radar}
                    </Button>
                </div>
                <div class="bw-tbl">
                    <div style={TABLE_SCROLLER_STYLE}>
                        <HomeDomainsPreview preview={preview} />
                    </div>
                </div>
                <HomeBrowseStrip figures={readHomeDemoStripFigures(preview.search.stats)} countPrecision={preview.search.stats?.countPrecision ?? 'exact'} />
            </section>
        </div>
    );
};

export default HomeBrowsePanel;
